/* ============================================================
   mediaGalleryView — ME-3 pure view model for product galleries
   (photo + video, marketplace-style) and covers.
   ============================================================ */

import type { ProductDetail, ProductSummary } from '../types/product';
import { galleryItemKind, type GalleryItemKind } from './shop3dView.ts';

export interface GalleryItem {
  /** Absolute-or-relative media URL for the main stage. */
  url: string;
  /** Compact thumbnail URL when the engine generated one. */
  thumbUrl: string;
  /** cca635b3: 'model' rows (viewable STL/OBJ/3MF URLs) mount the 3D stage. */
  kind: GalleryItemKind;
}

export interface GalleryView {
  items: GalleryItem[];
  /** Index of the item the stage should show first (first image, else 0). */
  initialIndex: number;
  /** Single cover URL for cards; null when there is nothing to show. */
  coverUrl: string | null;
}

const fromMedia = (media: ProductDetail['media'] | null | undefined): GalleryItem[] =>
  (media ?? [])
    .filter((m) => Boolean(m?.url))
    .map((m) => ({
      url: m.url,
      thumbUrl: m.thumbnailUrl ?? m.url,
      kind: galleryItemKind(m.url, m.kind),
    }));

/**
 * Gallery for a product detail: engine media first (input order), the legacy
 * single imageUrl appended as a fallback image when present, never duplicates.
 */
export function mediaGalleryView(product: Pick<ProductDetail, 'media' | 'imageUrl'>): GalleryView {
  const items = fromMedia(product.media);
  if (product.imageUrl && !items.some((i) => i.url === product.imageUrl)) {
    items.push({ url: product.imageUrl, thumbUrl: product.imageUrl, kind: 'image' });
  }

  const firstImage = items.findIndex((i) => i.kind === 'image');
  const initialIndex = firstImage >= 0 ? firstImage : 0;

  return { items, initialIndex, coverUrl: items[initialIndex]?.thumbUrl ?? null };
}

/** Catalog card cover: backend cover first, legacy image as fallback. */
export function summaryCoverUrl(p: Pick<ProductSummary, 'coverUrl' | 'imageUrl'>): string | null {
  return p.coverUrl ?? p.imageUrl ?? null;
}

/** Small square logo URL for provider cards (squares the engine thumbnail). */
export function providerLogoView(logoUrl: string | null | undefined): string | null {
  const trimmed = (logoUrl ?? '').trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('/media/') && !trimmed.startsWith('https://')) return null;
  return trimmed;
}
